import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  getOrders,
  confirmOrder,
  getConfirmationAgents,
  assignOrder,
  deleteOrder,
} from "../services/api";
import SyncButton from "../components/SyncButton";
import ConfirmationModal from "../components/ConfirmationModal";
import TrackingModal from "../components/TrackingModal";
import { OrdersSkeletonList } from "../components/OrderSkeleton";
import SearchableSelect from "../components/SearchableSelect";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  Search,
  Package,
  Phone,
  MapPin,
  Clock,
  ChevronRight,
  Filter,
  DollarSign,
  ShoppingCart,
  Clock3,
  MoreVertical,
  Eye,
  ExternalLink,
  Plus,
  User as UserIcon,
  Trash2,
} from "lucide-react";
import DuplicateBadge from "../components/DuplicateBadge";
import { formatDate } from "../utils/dateUtils";
import { formatSize } from "../utils/variantUtils";

// Helper components for stats
const StatCard = ({ title, value, icon: Icon, colorClass, bgColorClass }) => (
  <div
    className={`bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between`}
  >
    <div>
      <p className="text-gray-400 text-[10px] font-semibold mb-1 uppercase tracking-widest">
        {title}
      </p>
      <h3 className="text-lg md:text-xl font-bold text-gray-900">{value}</h3>
    </div>
    <div
      className={`w-10 h-10 rounded-xl ${bgColorClass} flex items-center justify-center ${colorClass}`}
    >
      <Icon size={20} />
    </div>
  </div>
);

export default function Orders() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "all",
  );
  const [agentFilter, setAgentFilter] = useState(
    searchParams.get("assignedTo") || "all",
  );
  // Confirmation Modal State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  // Pagination State
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page")) || 1,
  );
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [summaryStats, setSummaryStats] = useState({
    totalRevenue: 0,
    pendingCount: 0,
  });
  const itemsPerPage = 20; // Default limit

  // Assignment State
  const [agents, setAgents] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assigningOrder, setAssigningOrder] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [isAssignLoading, setIsAssignLoading] = useState(false);

  // Tracking Modal State
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null);

  const handleOpenConfirmation = (order) => {
    setSelectedOrder(order);
    setIsReadOnly(user?.role === "gerant"); // Force read-only for gerant
    setIsModalOpen(true);
  };

  const handleOpenDetails = (order) => {
    setSelectedOrder(order);
    setIsReadOnly(true);
    setIsModalOpen(true);
  };

  const handleTrackOrder = (order) => {
    setTrackingOrder(order);
    setIsTrackingModalOpen(true);
  };

  const handleSaveConfirmation = async (orderId, confirmationData) => {
    try {
      await confirmOrder(orderId, confirmationData);
      fetchOrders(); // Refresh list
    } catch (err) {
      console.error("Failed to save confirmation:", err);
      throw err; // Passed to modal to show error
    }
  };

  const fetchAgents = React.useCallback(async () => {
    try {
      const { data } = await getConfirmationAgents();
      setAgents(data);
    } catch (err) {
      console.error("Failed to fetch agents:", err);
    }
  }, []);

  const handleDeleteOrder = async (order) => {
    if (
      window.confirm(
        t("orders.messages.confirm_delete_message", { orderId: order.orderId }),
      )
    ) {
      try {
        await deleteOrder(order._id);
        fetchOrders(); // Refresh list
      } catch (err) {
        console.error("Failed to delete order:", err);
        alert(t("products.messages.delete_failed"));
      }
    }
  };

  const handleAssignAgent = async (orderId, agentId) => {
    try {
      setIsAssignLoading(true);
      await assignOrder(orderId, agentId);
      setIsAssignModalOpen(false);
      setAssigningOrder(null);
      fetchOrders(); // Refresh list
    } catch (err) {
      console.error("Failed to assign agent:", err);
      alert(t("common.operation_failed"));
    } finally {
      setIsAssignLoading(false);
    }
  };

  const fetchOrders = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getOrders({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter !== "all" ? statusFilter : undefined,
        assignedTo: agentFilter !== "all" ? agentFilter : undefined,
        search: debouncedSearch || undefined,
      });

      if (res.success) {
        setOrders(res.data);
        setTotalItems(res.pagination.total);
        setTotalPages(res.pagination.totalPages);
        if (res.summary) {
          setSummaryStats(res.summary);
        }
      }
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, agentFilter, debouncedSearch]);

  useEffect(() => {
    if (isModalOpen || isAssignModalOpen || isTrackingModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen, isAssignModalOpen, isTrackingModalOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const params = { page: currentPage, limit: itemsPerPage };
    if (statusFilter !== "all") params.status = statusFilter;
    if (agentFilter !== "all") params.assignedTo = agentFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    setSearchParams(params);
    fetchOrders();
  }, [
    currentPage,
    statusFilter,
    agentFilter,
    debouncedSearch,
    fetchOrders,
    setSearchParams,
  ]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const stats = useMemo(() => {
    const isAgent = user?.role === "confirmation";
    return {
      total: totalItems,
      pending: summaryStats.pendingCount,
      revenue: summaryStats.totalRevenue,
      commission: summaryStats.totalCommission,
      isAgent,
    };
  }, [totalItems, summaryStats, user]);

  // Pagination Logic - orders are already filtered by the backend

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 md:p-8 pt-5 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
            {t("orders.title")}
          </h1>
          <p className="text-gray-400 font-medium mt-0.5 text-xs md:text-sm">
            {t("orders.subtitle", { count: totalItems })}
          </p>
        </div>
        {user?.role !== "gerant" && (
          <div className="flex items-center gap-2.5 w-full md:w-auto">
            <div className="flex-1 md:flex-none">
              <SyncButton onSyncSuccess={fetchOrders} />
            </div>
            <Link
              to="/orders/new"
              className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-gray-900 hover:bg-indigo-600 text-white md:px-4 px-2 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 shadow-md shadow-gray-200"
            >
              <Plus size={16} />
              <span>{t("orders.actions.create")}</span>
            </Link>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          title={
            stats.isAgent
              ? t("orders.stats.my_total", { defaultValue: "My Assigned" })
              : t("orders.stats.total")
          }
          value={stats.total}
          icon={ShoppingCart}
          colorClass="text-indigo-600"
          bgColorClass="bg-indigo-50"
        />
        <StatCard
          title={
            stats.isAgent
              ? t("orders.stats.my_pending", { defaultValue: "My Pending" })
              : t("orders.stats.pending")
          }
          value={stats.pending}
          icon={Clock3}
          colorClass="text-amber-600"
          bgColorClass="bg-amber-50"
        />
        {user?.role !== "gerant" && (
          <StatCard
            title={
              stats.isAgent
                ? t("orders.stats.total_commission", {
                    defaultValue: "Total Commission",
                  })
                : t("orders.stats.revenue")
            }
            value={`${((stats.isAgent ? stats.commission : stats.revenue) || 0).toLocaleString()} ${t("common.currency")}`}
            icon={DollarSign}
            colorClass="text-emerald-600"
            bgColorClass="bg-emerald-50"
          />
        )}
      </div>

      {/* Control Bar */}
      <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm mb-6 flex flex-col md:flex-row gap-3 items-center">
        <div className="relative w-full">
          <Search
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            type="text"
            placeholder={t("orders.placeholders.search")}
            className="w-full pl-10 pr-4 py-3 md:py-2.5 rounded-xl bg-gray-50/80 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-xs md:text-sm"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="flex items-center gap-2.5 w-full md:w-auto">
          <Filter className="text-gray-400 ml-1 hidden md:block" size={16} />
          <select
            className="w-full md:w-44 px-3.5 py-3 md:py-2.5 rounded-xl bg-gray-50/80 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-xs md:text-sm text-gray-700"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t("orders.filters.status_all")}</option>
            <option value="pending">
              {t("orders.filters.status.pending")}
            </option>
            <option value="confirmed_ready_for_shipping">
              {t("orders.filters.status.confirmed")}
            </option>
            <option value="appel_1">
              {t("orders.filters.status.appel_1")}
            </option>
            <option value="appel_2">
              {t("orders.filters.status.appel_2")}
            </option>
            <option value="appel_3">
              {t("orders.filters.status.appel_3")}
            </option>
            <option value="call_whatsapp">
              {t("orders.filters.status.call_whatsapp")}
            </option>
            <option value="call_sms">
              {t("orders.filters.status.call_sms")}
            </option>
            <option value="no_answer">
              {t("orders.filters.status.no_answer")}
            </option>
            <option value="postponed">
              {t("orders.filters.status.postponed")}
            </option>
            <option value="unreachable">
              {t("orders.filters.status.unreachable")}
            </option>
            <option value="voicemail">
              {t("orders.filters.status.voicemail")}
            </option>
            <option value="cancelled">
              {t("orders.filters.status.cancelled")}
            </option>
            <option value="not_serious">
              {t("orders.filters.status.not_serious")}
            </option>
            <option value="fake_order">
              {t("orders.filters.status.fake_order")}
            </option>
            <option value="double_order">
              {t("orders.filters.status.double_order")}
            </option>
            <option value="sent_to_delivery">
              {t("orders.filters.status.sent_to_delivery")}
            </option>
          </select>
          {isAdmin && (
            <SearchableSelect
              options={agents.map((a) => ({ id: a._id, name: a.name }))}
              value={agentFilter}
              onChange={setAgentFilter}
              allOptionLabel={t("orders.filters.agent_all")}
              placeholder={t("orders.filters.agent_label")}
              searchPlaceholder={t("common.search")}
              noResultsText={t("common.no_data")}
            />
          )}
        </div>
      </div>

      {/* Orders View */}
      {loading ? (
        <OrdersSkeletonList count={8} />
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package size={28} className="text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">
            {t("orders.messages.no_data_title")}
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            {t("orders.messages.no_data_subtitle")}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 mb-6">
            {orders.map((order) => (
              <div
                key={order._id}
                className={`group rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col md:flex-row relative ${
                  order.isPotentialDuplicate
                    ? "bg-amber-50/50 border-amber-200 shadow-amber-100/20 shadow-md"
                    : "bg-white border-gray-100 shadow-sm hover:shadow-lg hover:shadow-indigo-500/5"
                }`}
              >
                {/* Status Side Accent */}
                <div
                  className={`w-1 md:w-1.5 ${
                    order.status === "confirmed" ||
                    order.status === "confirmed_ready_for_shipping" ||
                    order.status === "call_whatsapp"
                      ? "bg-green-500"
                      : order.status === "cancelled" ||
                          order.status === "unreachable" ||
                          order.status === "not_serious"
                        ? "bg-red-500"
                        : order.status === "sent_to_delivery" ||
                            order.status === "call_sms"
                          ? "bg-indigo-500"
                          : order.status === "appel_1" ||
                              order.status === "appel_2" ||
                              order.status === "appel_3"
                            ? "bg-amber-500"
                            : order.status === "voicemail"
                              ? "bg-teal-500"
                              : order.status === "postponed"
                                ? "bg-blue-500"
                                : order.status === "fake_order" ||
                                    order.status === "double_order"
                                  ? "bg-gray-500"
                                  : "bg-amber-500"
                  }`}
                />

                <div className="p-5 md:p-6 flex-1 flex flex-col gap-4">
                  {/* Main Row */}
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-500 transition-colors overflow-hidden border border-gray-100/50 shrink-0">
                        {order.productImage ? (
                          <img
                            src={order.productImage}
                            alt={order.productName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.parentElement.innerHTML =
                                '<div class="text-gray-400"><Package size={28} /></div>';
                            }}
                          />
                        ) : (
                          <Package size={22} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                          <span className="font-bold text-base md:text-lg text-gray-900 tracking-tight">
                            #{order.orderId}
                          </span>
                          <div
                            className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-widest ${
                              order.status === "confirmed" ||
                              order.status === "confirmed_ready_for_shipping"
                                ? "bg-green-100 text-green-700"
                                : order.status === "cancelled" ||
                                    order.status === "unreachable"
                                  ? "bg-red-100 text-red-700"
                                  : order.status === "sent_to_delivery"
                                    ? "bg-indigo-100 text-indigo-700"
                                    : order.status === "appel_1" ||
                                        order.status === "appel_2" ||
                                        order.status === "appel_3"
                                      ? "bg-amber-100 text-amber-700"
                                      : order.status === "call_whatsapp"
                                        ? "bg-green-100 text-green-700"
                                        : order.status === "call_sms"
                                          ? "bg-indigo-100 text-indigo-700"
                                          : order.status === "voicemail"
                                            ? "bg-teal-100 text-teal-700"
                                            : order.status === "not_serious"
                                              ? "bg-rose-100 text-rose-700"
                                              : order.status === "fake_order" ||
                                                  order.status ===
                                                    "double_order"
                                                ? "bg-slate-100 text-slate-700"
                                                : order.status === "no_answer"
                                                  ? "bg-gray-100 text-gray-700"
                                                  : order.status === "postponed"
                                                    ? "bg-blue-100 text-blue-700"
                                                    : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {t(`status.order.${order.status || "pending"}`)}
                          </div>
                          <DuplicateBadge order={order} />
                          {order.delivery?.status && (
                            <div className="px-2.5 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-100">
                              {t(
                                `status.delivery.${order.delivery.status.toLowerCase()}`,
                                { defaultValue: order.delivery.status },
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 text-[13px] md:text-sm font-medium text-gray-500">
                          <span className="flex items-center gap-1">
                            <Phone size={13} className="text-gray-300" />{" "}
                            {order.phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={13} className="text-gray-300" />{" "}
                            {order.city}
                          </span>
                          <span className="flex items-center gap-1">
                            <UserIcon size={13} className="text-gray-300" />
                            {order.assignedTo ? (
                              <span className="text-indigo-600 font-semibold">
                                {t("orders.table.assigned_to", {
                                  name: order.assignedTo.name,
                                })}
                              </span>
                            ) : (
                              <span className="text-amber-500 font-semibold italic">
                                {t("orders.table.unassigned")}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto mt-2 md:mt-0">
                      <div className="md:text-right">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                          {t("orders.table.total_amount")}
                        </p>
                        <p className="text-lg md:text-xl font-bold text-indigo-600 tracking-tight">
                          {(order.price || 0).toLocaleString()}{" "}
                          <span className="text-xs font-semibold">
                            {t("common.currency")}
                          </span>
                        </p>
                      </div>
                      {isAdmin && user?.role !== "gerant" && (
                        <div className="relative group/menu">
                          <button className="p-2 text-gray-300 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50">
                            <MoreVertical size={18} />
                          </button>
                          <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1 opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all z-20">
                            <button
                              onClick={() => {
                                setAssigningOrder(order);
                                setSelectedAgentId(order.assignedTo?._id || "");
                                setIsAssignModalOpen(true);
                              }}
                              className="w-full text-left px-3.5 py-2 hover:bg-gray-50 text-sm font-semibold text-gray-700 flex items-center gap-2"
                            >
                              <UserIcon size={14} />
                              {t("orders.actions.update_agent")}
                            </button>
                            <button
                              onClick={() => handleDeleteOrder(order)}
                              className="w-full text-left px-3.5 py-2 hover:bg-red-50 text-sm font-semibold text-red-600 flex items-center gap-2"
                            >
                              <UserIcon size={14} />
                              {t("orders.actions.delete")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Info Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-y border-gray-50">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                        {t("orders.table.product_details")}
                      </p>
                      <p className="font-semibold text-[13px] md:text-sm text-gray-900 line-clamp-1">
                        {order.productName}
                      </p>
                      <p className="text-[11px] md:text-xs text-gray-400 font-medium">
                        <bdi className="font-bold text-gray-700">
                          {(() => {
                            const v = order.shipping?.variant || order.variant;
                            if (v && typeof v === 'object') {
                                return [v.color, formatSize(v.size)].filter(Boolean).join(' - ');
                            }
                            return v || t("orders.table.standard_variant");
                          })()}
                        </bdi>{" "}
                        • {t("orders.table.qty", { count: order.quantity })}
                      </p>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                        {t("orders.table.order_time")}
                      </p>
                      <div className="flex items-center gap-1.5 text-gray-700 font-semibold text-[13px] md:text-sm">
                        <Clock size={14} className="text-indigo-400" />
                        <span>
                          {formatDate(order.orderDate, i18n.language)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2.5 flex-wrap">
                      {order.status === "sent_to_delivery" ? (
                        <>
                          <button
                            onClick={() => handleOpenDetails(order)}
                            className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-4 py-2 rounded-xl font-semibold text-[13px] md:text-sm transition-all border border-indigo-100/50"
                          >
                            <Eye size={15} />
                            <span>{t("orders.actions.details")}</span>
                          </button>
                          <button
                            onClick={() => handleTrackOrder(order)}
                            className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-gray-900 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl font-semibold text-[13px] md:text-sm transition-all duration-300 shadow-md shadow-gray-200"
                          >
                            <ExternalLink size={15} />
                            <span>{t("orders.actions.track")}</span>
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleOpenConfirmation(order)}
                          className={`w-full md:w-auto flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl font-semibold text-[13px] md:text-sm transition-all duration-300 shadow-md shadow-gray-200 ${
                            user?.role === "gerant"
                              ? "bg-indigo-50 text-indigo-700 border border-indigo-100/50 hover:bg-indigo-100"
                              : "bg-gray-900 hover:bg-indigo-600 text-white"
                          }`}
                        >
                          {user?.role === "gerant" ? (
                            <>
                              <Eye size={15} />
                              <span>{t("orders.actions.details")}</span>
                            </>
                          ) : (
                            <>
                              <span>{t("orders.actions.confirm")}</span>
                              <ChevronRight size={15} />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8 mb-6">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 w-10 h-10 flex items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm text-gray-400 hover:bg-indigo-600 hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-gray-400 transition-all duration-300"
              >
                <ChevronRight size={16} className="rotate-180" />
              </button>

              <div className="flex items-center gap-1.5 px-4 py-1.5 bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto max-w-[250px] md:max-w-none scrollbar-hide">
                {[...Array(totalPages)].map((_, i) => {
                  const pageNumber = i + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg font-bold text-sm transition-all duration-300 ${
                          currentPage === pageNumber
                            ? "bg-indigo-600 text-white shadow-md shadow-indigo-100"
                            : "text-gray-400 hover:bg-indigo-50 hover:text-indigo-600"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }

                  if (
                    (pageNumber === 2 && currentPage > 3) ||
                    (pageNumber === totalPages - 1 &&
                      currentPage < totalPages - 2)
                  ) {
                    return (
                      <span
                        key={pageNumber}
                        className="text-gray-300 px-0.5 font-semibold text-sm"
                      >
                        ...
                      </span>
                    );
                  }

                  return null;
                })}
              </div>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="p-2 w-10 h-10 flex items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm text-gray-400 hover:bg-indigo-600 hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-gray-400 transition-all duration-300"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          <div className="text-center text-gray-300 text-[10px] font-semibold uppercase tracking-widest pb-8">
            {t("orders.pagination.showing", {
              start: (currentPage - 1) * itemsPerPage + 1,
              end: Math.min(currentPage * itemsPerPage, totalItems),
              total: totalItems,
            })}
          </div>
        </>
      )}
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        order={selectedOrder}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveConfirmation}
        readOnly={isReadOnly}
      />

      {/* Tracking Modal */}
      <TrackingModal
        isOpen={isTrackingModalOpen}
        onClose={() => {
          setIsTrackingModalOpen(false);
          setTrackingOrder(null);
        }}
        order={trackingOrder}
      />

      {/* Assignment Modal */}
      {isAssignModalOpen && assigningOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                    {t("orders.actions.update_agent")}
                  </h3>
                  <p className="text-gray-400 text-sm font-semibold mt-0.5">
                    #{assigningOrder.orderId}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
                  <UserIcon size={20} />
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
                    {t("create_order.form.agent")}
                  </label>
                  <select
                    className="w-full px-4 py-3 rounded-xl bg-gray-50/80 border border-gray-100 focus:ring-2 focus:ring-indigo-500 font-semibold text-sm text-gray-700 appearance-none cursor-pointer"
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    disabled={isAssignLoading}
                  >
                    <option value="" disabled>
                      {t("create_order.form.placeholders.select_agent")}
                    </option>
                    {agents.map((agent) => (
                      <option key={agent._id} value={agent._id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setIsAssignModalOpen(false);
                      setAssigningOrder(null);
                      setSelectedAgentId("");
                    }}
                    className="flex-1 py-3 rounded-xl font-semibold text-gray-500 hover:bg-gray-50 transition-all text-sm"
                    disabled={isAssignLoading}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={() =>
                      handleAssignAgent(assigningOrder._id, selectedAgentId)
                    }
                    className="flex-1 py-3 rounded-xl font-bold bg-gray-900 hover:bg-indigo-600 text-white transition-all duration-300 shadow-md shadow-gray-200 text-sm disabled:opacity-50"
                    disabled={
                      isAssignLoading ||
                      !selectedAgentId ||
                      selectedAgentId === assigningOrder.assignedTo?._id
                    }
                  >
                    {isAssignLoading ? t("common.loading") : t("common.save")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
